import React from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {usePageNav, type PageLink} from '@/shared/lib/use-page-nav';
import {useRailStore} from '@/shared/model/rail-store';
import {IconButton} from '@/shared/ui/button';
import {ButtonLayoutContext} from '@/shared/ui/button-layout';
import {ErrorBoundary} from '@/shared/ui/error-boundary';
import {Icon} from '@/shared/ui/icons';
import {
  InspectorPanel,
  InspectorProvider,
  useInspectorHostState,
} from '@/shared/ui/inspector';
import {AppText} from '@/shared/ui/text';
import {layout, radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Каркас страницы. Это shadcn-блок `sidebar-07` + `dashboard-01`, собранный на RN:
 *
 *      ┌─ верхняя полоса ─────────────────────────────────────────┐┌─ инспектор ─┐
 *      │ ☰ │ Настройки › Отделы                              ⟳  ││ Имя       × │
 *      ├──────────────────────────────────────────────────────────┤│             │
 *      │  Отделы                                     [ Добавить ] ││  карточка   │
 *      │  12 подразделений                                        ││             │
 *      │  ──────────── тулбар: поиск · фильтры ────────────────── ││             │
 *      │  содержимое                                              ││             │
 *      └──────────────────────────────────────────────────────────┘└─────────────┘
 *
 * ## Три слоя, и у каждого одна работа
 *
 * **Верхняя полоса** (только на широком окне) — рама: кнопка панели, маршрут
 * («где я» без единого клика, каждая ступень нажимается) и «обновить». Она НЕ
 * прокручивается вместе с содержимым: маршрут и выход из экрана должны быть на месте,
 * куда бы ни докрутили таблицу.
 *
 * **Шапка страницы** — заголовок, строка фактов под ним и кнопки действий справа.
 * Заголовок ровно один, и он живёт в потоке страницы. Действия — ТОЛЬКО здесь: раньше
 * «+ Сотрудник» был нарисован и в шапке, и в полосе над таблицей, на одном экране
 * дважды.
 *
 * **Инспектор** — панель с выбранной записью на всю высоту, справа от прокрутки, а не
 * внутри неё. Есть только пока есть что показать. См. `shared/ui/inspector`.
 *
 * На телефоне полосы нет (её работу делает `AppHeader` и вкладки), шапка та же, а
 * вместо маршрута — одна кнопка «← Родитель»: куда ведёт стрелка, видно до нажатия.
 *
 * ## Маршрут страница берёт сама
 *
 * Из карты родства экранов ([`shared/config/screens`](../config/screens.ts)) через
 * [`usePageNav`](../lib/use-page-nav.ts). Экран не решает, куда ведёт «назад», —
 * стрелка ведёт на уровень вверх, откуда бы на экран ни пришли; `onBack` остался
 * исключением для экранов с формой внутри (лист закрывается раньше страницы).
 *
 * ## Ширина
 *
 * `auto` — колонка на телефоне, вся ширина окна на десктопе (списки, доски).
 * `form` — читаемая колонка для одной сущности или страницы настроек.
 * `wide` — форма в две колонки (главное + сбоку): шире документа, уже списка.
 * `narrow` — вход и онбординг: одна короткая задача, которая не растёт с окном.
 */
export type PageWidth = 'auto' | 'form' | 'wide' | 'narrow';

export type PageProps = {
  children: React.ReactNode;
  /** Имя страницы. Единственный заголовок на экране. */
  title?: string;
  /**
   * Не печатать заголовок в теле: имя остаётся во вкладке браузера и в верхней
   * полосе десктопа. Для экранов-пультов, где каждая строка над содержимым отнята у
   * него, — просмотр камер.
   */
  hideTitle?: boolean;
  /** Строка фактов под заголовком: сколько, за какой период, чей. */
  subtitle?: string | null;
  /** Метка слева от заголовка — лицо человека на его собственном отчёте. */
  mark?: React.ReactNode;
  /**
   * Перехват «назад» — ТОЛЬКО для страниц, у которых внутри есть что закрыть раньше
   * самой страницы. Обычной странице задавать его не нужно.
   */
  onBack?: () => void;
  /** Подпись у стрелки. По умолчанию — имя родительского экрана. */
  backLabel?: string;
  /** Спрятать маршрут. Для экранов вне карты (онбординг, заглушки). */
  trail?: boolean;
  /** Кнопки справа от заголовка (на телефоне — строкой под ним). */
  actions?: React.ReactNode;
  /** Полоса под заголовком: период, фильтры, сегменты. */
  toolbar?: React.ReactNode;
  /**
   * Колонка слева от содержимого на широком окне — навигация раздела настроек.
   * На телефоне не рисуется: там её работу делает список-хаб.
   */
  sideNav?: React.ReactNode;
  /** Обернуть содержимое в ScrollView. */
  scroll?: boolean;
  /** Потянуть-обновить. Работает только вместе со `scroll`. */
  refreshing?: boolean;
  onRefresh?: () => void;
  width?: PageWidth;
  /**
   * Убрать боковые отступы У СОДЕРЖИМОГО — для списков, которые рисуют свои края
   * сами. Заголовок отступ платит в любом случае.
   */
  gutter?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

/** Высота верхней полосы — shadcn `h-14`. */
const TOP_BAR = 56;

const styles = StyleSheet.create({
  root: {flex: 1, flexDirection: 'row'},
  main: {flex: 1, minWidth: 0},
  flex: {flex: 1},
  column: {flex: 1, width: '100%', alignSelf: 'center'},
  scrollBody: {flexGrow: 1, width: '100%', alignSelf: 'center', paddingBottom: spacing(10)},
  phone: {maxWidth: layout.maxContentWidth},
  form: {maxWidth: layout.formContentWidth},
  wideForm: {maxWidth: layout.wideFormContentWidth},
  narrow: {maxWidth: layout.authContentWidth},
  wide: {maxWidth: '100%'},

  /* Верхняя полоса: кнопка панели, черта, маршрут, справа «обновить». */
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: TOP_BAR,
    paddingHorizontal: spacing(4),
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  topBarSep: {width: StyleSheet.hairlineWidth, height: 16, marginHorizontal: spacing(1)},
  topBarSpacer: {flex: 1},

  /* Маршрут. Переносится по словам: «Настройки › Оборудование › Профили» в
     узкой полосе не помещается ни при каком кегле. */
  crumbs: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
  },
  crumb: {paddingVertical: 4, paddingHorizontal: spacing(1) + 2, borderRadius: radii.sm},
  crumbSep: {paddingHorizontal: 2, transform: [{rotate: '0deg'}]},

  head: {gap: spacing(3), paddingTop: spacing(4), paddingBottom: spacing(4)},
  headDesktop: {paddingTop: spacing(6), paddingBottom: spacing(5)},
  /* «Назад» — КНОПКА с именем родителя, а не строчка серого текста. Только на
     телефоне: на широком окне маршрут стоит в верхней полосе. */
  back: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    alignSelf: 'flex-start',
    minHeight: 32,
    paddingVertical: spacing(1),
    paddingLeft: spacing(2),
    paddingRight: spacing(3),
    marginLeft: -spacing(2),
    borderRadius: radii.md,
  },
  titleRow: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(4)},
  mark: {paddingTop: 2},
  titleWrap: {flex: 1, minWidth: 0, gap: 2},
  actions: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), flexWrap: 'wrap'},
  actionsDesktop: {justifyContent: 'flex-end', paddingTop: 2},
  /* Полоса под заголовком. `gap` — не украшение: экран отдаёт сюда НЕСКОЛЬКО
     контролов, и без него они склеиваются в одно пятно. */
  toolbar: {gap: spacing(3), paddingBottom: spacing(4)},

  /* Настройки: колонка навигации слева, документ справа. */
  withSideNav: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(10)},
  sideNav: {width: 236, flexShrink: 0},
  sideNavContent: {flex: 1, minWidth: 0},
});

/** Курсор над контролом или палец на нём. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/** Ступень маршрута — ссылка. */
function Crumb({
  label,
  onPress,
  current = false,
  testID,
}: {
  label: string;
  onPress?: () => void;
  current?: boolean;
  testID?: string;
}) {
  const p = useTheme();
  if (!onPress) {
    return (
      <View style={styles.crumb} testID={testID}>
        <AppText variant="body" color={current ? p.foreground : undefined} numberOfLines={1}>
          {label}
        </AppText>
      </View>
    );
  }
  return (
    <Pressable
      testID={testID}
      accessibilityRole="link"
      accessibilityLabel={label}
      onPress={onPress}
      hitSlop={4}
      style={state => [styles.crumb, isHot(state) && {backgroundColor: p.accent}]}>
      {state => (
        <AppText
          variant="body"
          color={isHot(state) ? p.foreground : p.mutedForeground}
          numberOfLines={1}>
          {label}
        </AppText>
      )}
    </Pressable>
  );
}

/**
 * Маршрут в верхней полосе — shadcn `Breadcrumb`: предки серым, текущая страница
 * в полной краске, между ними шевроны. Каждый предок нажимается.
 */
function Breadcrumb({links, current}: {links: PageLink[]; current?: string}) {
  const p = useTheme();
  return (
    <View style={styles.crumbs} testID="page-trail">
      {links.map((link, i) => (
        <React.Fragment key={`${link.label}-${i}`}>
          {i > 0 ? (
            <View style={styles.crumbSep}>
              <Icon name="chevron" size={12} color={p.mutedForeground} />
            </View>
          ) : null}
          <Crumb
            label={link.label}
            onPress={link.onPress}
            testID={i === links.length - 1 ? 'back-link' : undefined}
          />
        </React.Fragment>
      ))}
      {current ? (
        <>
          {links.length > 0 ? (
            <View style={styles.crumbSep}>
              <Icon name="chevron" size={12} color={p.mutedForeground} />
            </View>
          ) : null}
          <Crumb label={current} current />
        </>
      ) : null}
    </View>
  );
}

/**
 * Верхняя полоса широкого окна. Кнопка панели — shadcn `SidebarTrigger`; маршрут —
 * `Breadcrumb`; справа — «обновить», единственный глагол, который относится к
 * читающему, а не к экрану.
 */
function TopBar({links, current}: {links: PageLink[]; current?: string}) {
  const {t} = useTranslation();
  const p = useTheme();
  const toggleRail = useRailStore(s => s.toggle);
  const queryClient = useQueryClient();

  return (
    <View
      testID="page-top-bar"
      style={[styles.topBar, {backgroundColor: p.background, borderBottomColor: p.border}]}>
      <IconButton
        testID="rail-toggle"
        icon="panel"
        size={32}
        label={t('shell.collapse')}
        onPress={toggleRail}
      />
      <View style={[styles.topBarSep, {backgroundColor: p.borderStrong}]} />
      <Breadcrumb links={links} current={current} />
      <View style={styles.topBarSpacer} />
      {/* Инвалидация, а не рефетч: панель, которая сейчас не смонтирована, не
          должна лезть в сеть только потому, что нажали кнопку. */}
      <IconButton
        testID="rail-refresh"
        icon="refresh"
        size={32}
        label={t('shell.refresh')}
        onPress={() => void queryClient.invalidateQueries()}
      />
    </View>
  );
}

/** Возвращает маршрут страницы с учётом перехвата «назад». */
function useTrail(onBack?: () => void, backLabel?: string) {
  const {t} = useTranslation();
  const nav = usePageNav();

  const back: PageLink | undefined = nav.back
    ? {label: backLabel ?? nav.back.label, onPress: onBack ?? nav.back.onPress}
    : onBack
    ? {label: backLabel ?? t('actions.back'), onPress: onBack}
    : undefined;

  const links: PageLink[] = onBack
    ? nav.trail.map((link, i) =>
        i === nav.trail.length - 1 ? {...link, onPress: onBack} : link,
      )
    : nav.trail;

  return {back, links};
}

/**
 * Шапка страницы отдельно от рамы — её берут списки, которые сами владеют скроллом
 * (`FlatList` не умеет жить внутри чужого `ScrollView`).
 */
export function PageHead({
  title,
  subtitle,
  mark,
  onBack,
  backLabel,
  actions,
  trail = true,
  style,
}: Pick<
  PageProps,
  'title' | 'subtitle' | 'mark' | 'onBack' | 'backLabel' | 'actions' | 'trail'
> & {
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  const {isTablet, isDesktop} = useBreakpoint();
  const {back} = useTrail(onBack, backLabel);

  // На широком окне маршрут стоит в верхней полосе; здесь — только на телефоне.
  const route =
    trail && !isDesktop && back ? (
      <Pressable
        testID="back-link"
        accessibilityRole="button"
        accessibilityLabel={back.label}
        onPress={back.onPress}
        hitSlop={8}
        style={state => [styles.back, isHot(state) && {backgroundColor: p.accent}]}>
        <Icon name="chevronLeft" size={16} color={p.mutedForeground} />
        <AppText variant="label" color={p.mutedForeground}>
          {back.label}
        </AppText>
      </Pressable>
    ) : null;

  if (!title && !route && !actions) {
    return null;
  }

  const verbs = actions ? (
    <View style={[styles.actions, isTablet && styles.actionsDesktop]}>{actions}</View>
  ) : null;

  return (
    <View style={[styles.head, isDesktop && styles.headDesktop, style]}>
      {route}

      <View style={styles.titleRow}>
        {mark ? <View style={styles.mark}>{mark}</View> : null}
        <View style={styles.titleWrap}>
          {title ? (
            <AppText variant="display" numberOfLines={2}>
              {title}
            </AppText>
          ) : null}
          {subtitle ? <AppText variant="caption">{subtitle}</AppText> : null}
        </View>
        {isTablet ? verbs : null}
      </View>

      {isTablet ? null : verbs}
    </View>
  );
}

export function Page({
  children,
  title,
  hideTitle = false,
  subtitle,
  mark,
  onBack,
  backLabel,
  actions,
  toolbar,
  sideNav,
  scroll = false,
  refreshing,
  onRefresh,
  width = 'auto',
  gutter = true,
  trail = true,
  style,
  testID,
}: PageProps) {
  const p = useTheme();
  const insets = useSafeAreaInsets();
  const {isDesktop} = useBreakpoint();
  const {links} = useTrail(onBack, backLabel);
  const inspector = useInspectorHostState();

  /**
   * Боковой отступ страницы. Считается ВСЕГДА: заголовок и панель под ним платят
   * его, даже когда содержимое рисует свои края само.
   */
  const pad = {
    paddingHorizontal: isDesktop ? layout.desktopGutter : layout.gutter,
  };
  const inset = gutter ? pad : null;
  const hasSideNav = isDesktop && !!sideNav;
  const widthStyle =
    width === 'narrow'
      ? styles.narrow
      : width === 'form' && !hasSideNav
      ? styles.form
      : width === 'wide' && !hasSideNav
      ? styles.wideForm
      : isDesktop
      ? styles.wide
      : styles.phone;

  const head = (
    <PageHead
      title={hideTitle ? undefined : title}
      subtitle={hideTitle ? undefined : subtitle}
      mark={mark}
      onBack={onBack}
      backLabel={backLabel}
      actions={actions}
      trail={trail}
      style={gutter ? undefined : pad}
    />
  );

  const document = (
    <>
      {head}
      {toolbar ? (
        <View style={[styles.toolbar, gutter ? undefined : pad]}>{toolbar}</View>
      ) : null}
      {/* Граница вокруг СОДЕРЖИМОГО, а не вокруг страницы целиком: экран, уронивший
          свой список, оставляет заголовок и маршрут на месте. */}
      <ErrorBoundary inline resetKey={title} testID="page-error-boundary">
        {children}
      </ErrorBoundary>
    </>
  );

  /*
    Навигация раздела слева от документа — shadcn «settings layout». Колонка
    навигации не прокручивается отдельно: страница настроек редко длиннее окна, а
    вторая полоса прокрутки рядом с первой — это то, чего каркас избегает везде.
  */
  const body = hasSideNav ? (
    <View style={[styles.withSideNav, pad]}>
      <View style={styles.sideNav}>{sideNav}</View>
      <View style={[styles.sideNavContent, styles.form]}>{document}</View>
    </View>
  ) : (
    document
  );

  const columnInset = hasSideNav ? null : inset;

  return (
    <InspectorProvider value={inspector.set}>
      {/* На широком окне кнопка в колонке обнимает подпись — см. `button-layout`. */}
      <ButtonLayoutContext.Provider value={isDesktop ? 'hug' : 'stretch'}>
      <View
        testID={testID}
        style={[styles.root, {backgroundColor: p.background, paddingBottom: insets.bottom}]}>
        <View style={styles.main}>
          {isDesktop && trail ? <TopBar links={links} current={title} /> : null}
          <KeyboardAvoidingView
            style={styles.flex}
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            {scroll ? (
              <ScrollView
                contentContainerStyle={[styles.scrollBody, widthStyle, columnInset, style]}
                showsVerticalScrollIndicator
                keyboardShouldPersistTaps="handled"
                refreshControl={
                  onRefresh ? (
                    <RefreshControl
                      refreshing={!!refreshing}
                      onRefresh={onRefresh}
                      tintColor={p.primary}
                      colors={[p.primary]}
                    />
                  ) : undefined
                }>
                {body}
              </ScrollView>
            ) : (
              <View style={[styles.column, widthStyle, columnInset, style]}>{body}</View>
            )}
          </KeyboardAvoidingView>
        </View>

        {isDesktop && inspector.entry ? <InspectorPanel entry={inspector.entry} /> : null}
      </View>
      </ButtonLayoutContext.Provider>
    </InspectorProvider>
  );
}
